import React, { memo, useMemo } from "react";
import { Translate } from "react-localize-redux";

import { PlayIcon } from "@pages/casino/assets/icons";

import {
	CardWrapper,
	GameAffiliateName,
	GameName,
	ImageWrapper,
	OverlayContainer,
	PlayIconGameNameWrapper,
	PlayOverlay,
} from "./elements.js";
import useController from "./useController.js";

const GameCard = memo(({ game, size, type, isGamePage, hasPlayIconAndGameName }) => {
	const {
		state: { imageSrc },
		handlers: { handleGameCardClick, handleImageError },
	} = useController({
		game,
		size,
		type,
		isGamePage,
		hasPlayIconAndGameName,
	});

	const windowConfig = useMemo(
		() => ({
			showSparkles: window.CASINO_GAME_HOVER_WAVE,
			showPlayIcon: window.CASINO_GAME_HOVER_PLAY_ICON,
			showColorfulShadow: window.CASINO_IMG_HOVER_HAS_COLORFUL_DROP_SHADOW,
			showHoverText: window.CASINO_GAME_HOVER_TEXT_VISIBLE,
			hasNoShadow: window.HOMEPAGE_AND_CASINO_PAGES_GAME_CARD_HAS_NO_SHADOW,
		}),
		[]
	);

	const renderSparkles = useMemo(() => {
		if (!windowConfig.showSparkles) return null;
		return (
			<>
				<div className="sparkle"></div>
				<div className="sparkle"></div>
				<div className="sparkle"></div>
				<div className="sparkle"></div>
			</>
		);
	}, [windowConfig.showSparkles]);

	const renderPlayOverlay = useMemo(() => {
		if (windowConfig.showPlayIcon) {
			if (hasPlayIconAndGameName || windowConfig.showColorfulShadow) {
				return (
					<PlayIconGameNameWrapper hasPlayIconAndGameName={hasPlayIconAndGameName}>
						{hasPlayIconAndGameName && (
							<GameAffiliateName>{game?.affiliate}</GameAffiliateName>
						)}
						<img src={windowConfig.showPlayIcon} />
						<GameName
							hasPlayIconAndGameName={hasPlayIconAndGameName}
							data-size={size}
							charLength={game?.remote_name?.length}>
							{game?.remote_name}
						</GameName>
					</PlayIconGameNameWrapper>
				);
			}
			return <img src={windowConfig.showPlayIcon} />;
		}
		return (
			<p>
				<PlayIcon />
				<Translate id="casino.letsplay" />
			</p>
		);
	}, [windowConfig.showPlayIcon, windowConfig.showColorfulShadow, hasPlayIconAndGameName, size]);

	return (
		<CardWrapper
			data-testid={`game-card-${game?.remote_name}`}
			hasPlayIconAndGameName={hasPlayIconAndGameName}
			hasNoShadow={windowConfig.hasNoShadow}>
			<ImageWrapper
				hasColorfulShadow={windowConfig.showColorfulShadow}
				id="game-image-wrapper"
				data-size={size}>
				<div className="img-container">
					<img
						data-size={size}
						className="lazyload big-image"
						data-src={imageSrc}
						loading="lazy"
						onError={handleImageError}
					/>
					{renderSparkles}
				</div>
				{!hasPlayIconAndGameName && (
					<GameAffiliateName>{game?.affiliate}</GameAffiliateName>
				)}
			</ImageWrapper>
			<OverlayContainer
				data-size={size}
				onClick={handleGameCardClick}>
				<PlayOverlay
					hasColorfulShadow={windowConfig.showColorfulShadow}
					id="play-overlay"
					data-size={size}>
					{renderPlayOverlay}
					{windowConfig.showHoverText && (
						<GameName
							data-size={size}
							charLength={game?.remote_name?.length}>
							{game?.remote_name}
						</GameName>
					)}
				</PlayOverlay>
			</OverlayContainer>
		</CardWrapper>
	);
});

GameCard.displayName = "GameCard";

export default GameCard;
